import { useState, useEffect, useRef } from "react";
import { X, Play, Pause, RotateCcw, TrendingUp, TrendingDown, Minus, Clock, DollarSign } from "lucide-react";
import { toast } from "sonner";

/**
 * Virtual Price Controller Modal
 * Allows setting a target price and duration for gradual price movement
 */
const VirtualPriceController = ({ instrument, onClose, onApply }) => {
  // Current state
  const [livePrice, setLivePrice] = useState(instrument.livePrice || instrument.currentPrice || 100);
  const [virtualPrice, setVirtualPrice] = useState(instrument.virtualPrice || livePrice);
  const [targetPrice, setTargetPrice] = useState(livePrice);
  const [duration, setDuration] = useState(5); // minutes
  const [isActive, setIsActive] = useState(instrument.virtualPriceActive || false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [startPrice, setStartPrice] = useState(null);
  
  // Animation ref
  const animationRef = useRef(null);

  // Calculate price direction
  const priceDirection = targetPrice > livePrice ? "up" : targetPrice < livePrice ? "down" : "neutral";
  const priceDiff = targetPrice - livePrice;
  const priceDiffPercent = livePrice > 0 ? ((priceDiff / livePrice) * 100).toFixed(2) : 0;

  // Update virtual price based on elapsed time
  useEffect(() => {
    if (isActive && startTime && startPrice !== null) {
      const updatePrice = () => {
        const now = Date.now();
        const elapsed = (now - startTime) / 1000 / 60; // elapsed minutes
        const progress = Math.min(elapsed / duration, 1);
        
        // Ease-in-out interpolation for smoother movement
        const easedProgress = progress < 0.5 
          ? 2 * progress * progress 
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        
        const newPrice = startPrice + (targetPrice - startPrice) * easedProgress;
        setVirtualPrice(parseFloat(newPrice.toFixed(instrument.decimals || 2)));
        
        // Calculate time remaining
        const remaining = Math.max(0, duration - elapsed);
        setTimeRemaining(remaining);
        
        if (progress >= 1) {
          // Animation complete
          setIsActive(false);
          setVirtualPrice(targetPrice);
          setTimeRemaining(0);
          toast.success(`${instrument.symbol} reached target price: ${targetPrice}`);
        } else {
          animationRef.current = requestAnimationFrame(updatePrice);
        }
      };
      
      animationRef.current = requestAnimationFrame(updatePrice);
      
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }
  }, [isActive, startTime, startPrice, targetPrice, duration, instrument.symbol, instrument.decimals]);

  // Start price movement
  const handleStart = () => {
    if (targetPrice === livePrice) {
      toast.error("Target price must be different from live price");
      return;
    }
    
    setStartTime(Date.now());
    setStartPrice(virtualPrice);
    setIsActive(true);
    setTimeRemaining(duration);
    
    toast.info(`Starting price movement for ${instrument.symbol}: ${virtualPrice} → ${targetPrice} over ${duration} min`);
  };

  // Pause price movement
  const handlePause = () => {
    setIsActive(false);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    toast.info(`Price movement paused at ${virtualPrice}`);
  };

  // Reset to live price
  const handleReset = () => {
    setIsActive(false);
    setVirtualPrice(livePrice);
    setTargetPrice(livePrice);
    setTimeRemaining(0);
    setStartTime(null);
    setStartPrice(null);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    toast.info(`${instrument.symbol} reset to live price: ${livePrice}`);
  };

  // Apply and close
  const handleApply = () => {
    onApply({
      symbol: instrument.symbol,
      livePrice,
      virtualPrice,
      targetPrice,
      duration,
      isActive,
      startTime,
      startPrice,
      timeRemaining
    });
    onClose();
  };

  // Format time remaining
  const formatTime = (minutes) => {
    const mins = Math.floor(minutes);
    const secs = Math.floor((minutes - mins) * 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[500px] max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-[#00BCD4] to-[#00ACC1]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <DollarSign className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{instrument.symbol}</h2>
              <p className="text-white/80 text-sm">{instrument.name || instrument.group}</p>
            </div>
          </div>
          <button className="text-white/80 hover:text-white text-2xl leading-none" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Price Display */}
          <div className="grid grid-cols-2 gap-4">
            {/* Live Price */}
            <div className="bg-gray-100 rounded-lg p-4">
              <label className="text-xs text-gray-500 uppercase tracking-wide">Live Price</label>
              <div className="text-2xl font-bold text-gray-800 mt-1">
                {livePrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-gray-400 mt-1">Market price</p>
            </div>
            
            {/* Virtual Price */}
            <div className={`rounded-lg p-4 ${isActive ? 'bg-blue-50 border-2 border-blue-300' : 'bg-gray-100'}`}>
              <label className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                Virtual Price
                {isActive && <span className="animate-pulse text-blue-500">LIVE</span>}
              </label>
              <div className={`text-2xl font-bold mt-1 ${
                virtualPrice > livePrice ? 'text-green-600' : 
                virtualPrice < livePrice ? 'text-red-600' : 'text-gray-800'
              }`}>
                {virtualPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {virtualPrice !== livePrice && (
                  <span className={virtualPrice > livePrice ? 'text-green-500' : 'text-red-500'}>
                    {virtualPrice > livePrice ? '+' : ''}{(virtualPrice - livePrice).toFixed(2)} 
                    ({virtualPrice > livePrice ? '+' : ''}{((virtualPrice - livePrice) / livePrice * 100).toFixed(2)}%)
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Progress Bar (when active) */}
          {isActive && (
            <div className="bg-gray-100 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 flex items-center gap-1">
                  <Clock size={14} />
                  Time Remaining
                </span>
                <span className="text-sm font-mono font-bold text-[#00BCD4]">
                  {formatTime(timeRemaining)}
                </span>
              </div>
              <div className="w-full bg-gray-300 rounded-full h-2">
                <div 
                  className="bg-[#00BCD4] h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${((duration - timeRemaining) / duration) * 100}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{startPrice?.toFixed(2)}</span>
                <span>{targetPrice.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Target Price Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              Target Price
              {priceDirection === "up" && <TrendingUp size={16} className="text-green-500" />}
              {priceDirection === "down" && <TrendingDown size={16} className="text-red-500" />}
              {priceDirection === "neutral" && <Minus size={16} className="text-gray-400" />}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                className="sirix-form-input flex-1 text-lg font-mono"
                value={targetPrice}
                onChange={(e) => setTargetPrice(parseFloat(e.target.value) || 0)}
                step={instrument.decimals === 5 ? 0.00001 : 0.01}
                disabled={isActive}
              />
              <div className="flex flex-col gap-1">
                <button 
                  className="px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded text-xs"
                  onClick={() => setTargetPrice(prev => parseFloat((prev * 1.01).toFixed(instrument.decimals || 2)))}
                  disabled={isActive}
                >
                  +1%
                </button>
                <button 
                  className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded text-xs"
                  onClick={() => setTargetPrice(prev => parseFloat((prev * 0.99).toFixed(instrument.decimals || 2)))}
                  disabled={isActive}
                >
                  -1%
                </button>
              </div>
            </div>
            {priceDiff !== 0 && (
              <p className={`text-sm ${priceDirection === "up" ? "text-green-600" : "text-red-600"}`}>
                {priceDirection === "up" ? "↑" : "↓"} {Math.abs(priceDiff).toFixed(2)} ({priceDiffPercent}%)
              </p>
            )}
          </div>

          {/* Duration Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Clock size={16} />
              Duration (minutes)
            </label>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 5, 10, 15, 30].map(min => (
                <button
                  key={min}
                  className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                    duration === min 
                      ? 'bg-[#00BCD4] text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  onClick={() => setDuration(min)}
                  disabled={isActive}
                >
                  {min}m
                </button>
              ))}
              <input
                type="number"
                className="sirix-form-input w-20 text-center"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value) || 1)}
                min="1"
                max="60"
                disabled={isActive}
              />
            </div>
          </div>

          {/* Control Buttons */}
          <div className="flex items-center justify-center gap-3 pt-4">
            {!isActive ? (
              <button
                className="flex items-center gap-2 px-6 py-3 bg-[#00BCD4] hover:bg-[#00ACC1] text-white rounded-lg font-medium transition-colors"
                onClick={handleStart}
              >
                <Play size={18} />
                Start Movement
              </button>
            ) : (
              <button
                className="flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
                onClick={handlePause}
              >
                <Pause size={18} />
                Pause
              </button>
            )}
            <button
              className="flex items-center gap-2 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
              onClick={handleReset}
            >
              <RotateCcw size={18} />
              Reset
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button 
            className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded font-medium"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="px-6 py-2 bg-[#00BCD4] hover:bg-[#00ACC1] text-white rounded font-medium"
            onClick={handleApply}
          >
            Apply & Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default VirtualPriceController;
